import React, { useEffect, useState, useRef, useCallback, Suspense } from 'react';
import Tilt from 'react-parallax-tilt';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Lazy-loaded components (must be outside component to avoid remount on re-render)
const Navbar = React.lazy(() => import('../../component/Navbar/NavBar'));
const Footer = React.lazy(() => import('../../component/Footer/Footer'));
const ScrollToTop = React.lazy(() => import('../../component/ScrollToTop'));

// ─── Generic Hook: Reveal on Scroll via IntersectionObserver ─────────────────
const useScrollReveal = (visibleClass, hiddenClass, threshold = 0.2) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add(visibleClass);
          el.classList.remove(hiddenClass);
        } else {
          el.classList.remove(visibleClass);
          el.classList.add(hiddenClass);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
};

// ─── FiturItem: isolated so each item has its own IntersectionObserver ────────
const FiturItem = ({ data, isEven }) => {
  // Image: 3D flip from left or right
  const flipClass  = isEven ? 'flip-from-left' : 'flip-from-right';
  const flipRef    = useScrollReveal('flip-visible', flipClass);

  // Text card: subtle slide from its own side
  // row-even → text on RIGHT → slides from right
  // row-odd  → text on LEFT  → slides from left
  const slideClass = isEven ? 'slide-from-right' : 'slide-from-left';
  const slideRef   = useScrollReveal('slide-visible', slideClass);

  return (
    <div className={`fitur-row ${isEven ? 'row-even' : 'row-odd'}`}>
      {/* Image: 3D Flip on Scroll */}
      <div ref={flipRef} className={`fitur-image-flip-wrapper ${flipClass}`}>
        <div className="fitur-image-wrapper">
          <img src={data.icon} alt={data.title} />
        </div>
      </div>

      {/* Text Card: subtle slide + Tilt Parallax on Hover */}
      <div ref={slideRef} className={`fitur-text-aos-wrapper ${slideClass}`}>
        <Tilt
          className="fitur-text-card"
          tiltMaxAngleX={6}
          tiltMaxAngleY={6}
          perspective={1000}
          transitionSpeed={1000}
          scale={1.015}
          gyroscope={true}
        >
          <h2>{data.title}</h2>
          <div className="fitur-subtitle">{data.subtitle}</div>
          <p className="fitur-desc">{data.description}</p>
        </Tilt>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Fitur = (props) => {
  const [navItems] = useState([
    { id: 1, idnm: '/', navheading: 'Beranda', active: false, box: false },
    { id: 2, idnm: 'fitur', navheading: 'Fitur', active: true, box: false },
    { id: 4, idnm: 'kontak', navheading: 'Kontak', active: false, box: false },
    { id: 6, idnm: 'register', navheading: 'Register', active: false, box: true },
  ]);
  const [pos] = useState(document.documentElement.scrollTop);
  const [imglight, setImglight] = useState(false);
  const [navClass, setNavClass] = useState('');

  const scrollNavigation = useCallback(() => {
    const scrollup = document.documentElement.scrollTop;
    if (scrollup > pos) {
      setNavClass('nav-sticky');
      setImglight(false);
    }
  }, [pos]);

  const daftarFitur =
    props.payload?.fiturs && props.payload.fiturs.length > 0
      ? props.payload.fiturs
      : [];

  useEffect(() => {
    AOS.init({ once: true, duration: 800 });
    window.addEventListener('scroll', scrollNavigation, true);
    return () => {
      window.removeEventListener('scroll', scrollNavigation, true);
    };
  }, [scrollNavigation]);

  return (
    <React.Fragment>
      <Suspense fallback={null}>
        <Navbar navItems={navItems} navClass={navClass} imglight={imglight} />
        <>
          <section className="fitur-hero">
            <div className="blob-1"></div>
            <div className="blob-2"></div>
            <Container>
              <h1
                className="hero-title"
                data-aos="zoom-in-up"
                data-aos-duration="1000"
              >
                Jelajahi Fitur <span>Unggulan</span> Kami
              </h1>
              <p
                className="hero-subtitle"
                data-aos="fade-up"
                data-aos-duration="1200"
                data-aos-delay="200"
              >
                Temukan berbagai layanan dan inovasi terbaik yang kami sediakan
                untuk memudahkan pengelolaan serta meningkatkan potensi desa
                Anda.
              </p>
            </Container>
          </section>

          <section className="fitur-overlap-section">
            <Container>
              {daftarFitur.map((data, i) => (
                <FiturItem key={i} data={data} isEven={i % 2 === 0} />
              ))}
            </Container>
          </section>
        </>
        <Footer />
        <ScrollToTop />
      </Suspense>
    </React.Fragment>
  );
};

const mapStateToProps = ({ mergeCms }) => ({
  payload: mergeCms.payload,
});
export default connect(mapStateToProps)(Fitur);

